import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [activeTenant, setActiveTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      // Set first tenant as active by default
      if (res.data.tenants?.length > 0) {
        const savedTenantId = localStorage.getItem('activeTenantId');
        const tenant = savedTenantId
          ? res.data.tenants.find(t => t.id === parseInt(savedTenantId)) || res.data.tenants[0]
          : res.data.tenants[0];
        switchTenant(tenant);
      }
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    if (res.data.user.tenants?.length > 0) {
      switchTenant(res.data.user.tenants[0]);
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('activeTenantId');
    delete api.defaults.headers.common['Authorization'];
    delete api.defaults.headers.common['X-Tenant-Id'];
    setToken(null);
    setUser(null);
    setActiveTenant(null);
  };

  const switchTenant = (tenant) => {
    setActiveTenant(tenant);
    localStorage.setItem('activeTenantId', tenant.id);
    api.defaults.headers.common['X-Tenant-Id'] = tenant.id;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  // Check if user has permission for a module/action
  const hasPermission = (moduleName, action) => {
    if (!user) return false;
    if (user.isSuperAdmin) return true;
    if (!activeTenant) return false;
    const perm = activeTenant.permissions?.find(p => p.module === moduleName);
    if (!perm) return false;
    const actionMap = { view: 'canView', create: 'canCreate', edit: 'canEdit', delete: 'canDelete', export: 'canExport' };
    return perm[actionMap[action]] || false;
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, activeTenant,
      login, logout, switchTenant, updateUser, hasPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
};
